import { Fragment, useEffect, useState } from 'react';
import {
    Create, SimpleForm, TextInput, useTranslate,
    FormDataConsumer, Toolbar, SaveButton,
    Button, AutocompleteInput, useNotify, useRefresh,
    useGetOne,
    SelectInput,
    ReferenceInput,
    useGetMany
} from 'react-admin';
import axios from 'axios';
import { makeStyles } from '@mui/styles';
import { Launch, Send } from '@material-ui/icons';
import { Dialog, DialogContent, DialogTitle, Box } from '@material-ui/core';
import { HandleError, ViewTitle } from '../../components';
import { Cancel } from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import CustomReferenceInput from '../../custom/customReferenceInput';

const useStyles = makeStyles((theme) => ({
    toolbar: {
        display: 'flex',
    },
    spacer: {
        flex: 1,
    },
    buttonMarginLeft: {
        marginLeft: 10
    },
    flexBox: {
        width: 'auto',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    inputMarginLeft: {
        marginLeft: 20
    },
    templateInput: {
        width: '300px',  /* Adjust this to your desired width */
    },
    border: {
        border: '1px solid #6b489d'
    }
}));

const Validate = (values) => {
    const errors = {};
    if (!values.name) errors.name = 'ra.validation.required';
    if (!values.pod) errors.pod = 'ra.validation.required';
    if (!values.instanceTemplate) errors.instanceTemplate = 'ra.validation.required';
    return errors;
};

// For Trainer view - Trainer should only see the instance template listed on the training template
const GetSelectedInstanceTemplate = ({ instanceTemplateIds }) => {
    const [selectedTemplateId, setSelectedTemplateId] = useState(null); 

    // Use useGetMany to fetch the instance templates based on the provided IDs
    const { data, isLoading, error } = useGetMany('instanceTemplates', { ids: instanceTemplateIds });

    // Only update selectedTemplateId when an ID is selected
    const handleSelectChange = (event) => {
        setSelectedTemplateId(event.target.value);
    };

    useEffect(() => {
        // Avoid setting state if there's no change
        if (!selectedTemplateId && data?.length) {
            setSelectedTemplateId(data[0]?.id); // Set a default value, if necessary
        }
    }, [data, selectedTemplateId]);

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error: {error.message}</div>;

    // Display the SelectInput with the fetched data (instance templates with name, language, version)
    return (
        <AutocompleteInput
            label="Instance Template"
            source="instanceTemplate"
            choices={data.map(template => ({
                id: template.id,  // Use the instance template ID
                name: `${template.name} #${template.language} #${template.snapshotVersion}`, // Display name, language, version
            }))}
            optionText="name"
            value={selectedTemplateId} // Use the selected value
            style={{ width: '300px' }}
        />
    );
};

const fetchTrainingTemplate = async (trainingTemplateId, podType) => {
    try {
        let type;
        if (podType === 'user') { type = 'userPod' }
        else { type = 'commonPod' };
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/v1/trainingtemplates/${trainingTemplateId}`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });

        // Assuming the training template has 'userPod' and 'commonPod' arrays
        if (response.data) {
            // Extract the correct pod list based on the podType (userPod or commonPod)
            const instanceTemplateList = response.data[type];

            // Map the podList to return only instanceTemplateId
            // Convert the instanceTemplateId strings into MongoDB ObjectId and store them
            const instanceTemplateIds = instanceTemplateList.map(entry => entry.instanceTemplateId);

            // Return the list of instanceTemplateIds
            return instanceTemplateIds;
        }
    } catch (error) {
        console.error('Error fetching room details:', error);
    }

};

const DeployInstanceTemplate = ({ permissions, ...props }) => {
    const classes = useStyles();
    const translate = useTranslate();
    const [showDialog, setShowDialog] = useState(false);
    const [trainingTemplate, setTrainingTemplate] = useState(null); // State to hold the training template info

    const [instanceTemplateIds, setInstanceTemplateIds] = useState([]); // Store the list of instanceTemplateIds

    const notify = useNotify();
    const refresh = useRefresh();
    const optionRenderer = choice => `${choice.name} #${choice.language} #${choice.snapshotVersion}`;

    const podId = props.pod.id;
    const podName = props.pod.podName;
    const type = props.pod.type;
    const roomId = props.pod.room;

    const filterToQuery = searchText => ({ name: `${searchText}` });
    // Fetch the room details using the room ID

    useEffect(() => {
        const fetchRoomDetails = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`/api/v1/classrooms/${roomId}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

                // Assuming the training template is under the room's details
                if (response.data && response.data.training_template) {
                    setTrainingTemplate(response.data.training_template);
                      // Avoid setting instanceTemplateIds if it's already set
                if (!instanceTemplateIds.length) {
                    let instanceTemplateList = await fetchTrainingTemplate(response.data.training_template, type);
                    setInstanceTemplateIds(instanceTemplateList);
                }
            }
            } catch (error) {
                console.error('Error fetching room details:', error);
                notify(translate('app.error.fetchRoomDetails'), { type: 'error' });
            }
        };

        if (roomId) {
            fetchRoomDetails();
        }
    }, [roomId, notify, translate]);

    const DeployTemplate = async (data) => {

        try {
            const options = {
                headers: {},
            };

            const sendData = {
                pod: data.pod,
                name: data.name,
                description: data.description,
                instanceTemplate: data.instanceTemplate
            };

            const token = localStorage.getItem('token');
            options.headers.Authorization = `Bearer ${token}`;

            const res = await axios.post(`/api/v1/instanceTemplates/deploy`, sendData, options);
            handleCloseClick();
            if (res.status !== 201) {
                notify(translate('app.instanceTemplates.deploy.errorMessage'), { type: 'error' });
            }
            else {
                notify(translate('app.instanceTemplates.deploy.successMessage'), { type: 'success' });
                handleCloseClick();
                refresh();
            };
        }
        catch (error) {
            HandleError(error, notify);
        }

    }


    const handleClick = () => {
        setShowDialog(true);
    };

    const handleCloseClick = () => {
        setShowDialog(false);
    };

    const postDefaultValue = () => ({
        pod: podName,
        //instanceTemplate: record.id,
        visibility: ['admin', 'designer', 'trainer', 'trainee'],
        //name: podName, description: `Instance from `
    });

    const GetTemplate = (id) => {
        const { data, isLoading, error } = useGetOne('instanceTemplates', { id });
        const { setValue } = useFormContext(); // Destructure setValue from useFormContext

        useEffect(() => {
            if (data) {
                const description = "Instance from " + data.name;
                // Use setValue to update the specific fields in the form
                setValue('name', data.name);
                setValue('description', description);
            }
        }, [data, setValue]); // Re-run this effect when data or setValue changes

        if (isLoading) { return <div>{translate('app.LoadingMessage')}</div>; }
        if (error) { return <div>{translate('app.ErrorMessage')}</div>; }

        return (
            <>
                <Box display={{ xs: 'block', sm: 'flex', width: '100%' }}>
                    <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
                        <TextInput
                            variant="outlined"
                            source="name"
                            fullWidth
                        />
                    </Box>
                    <Box flex={1} ml={{ xs: 0, sm: '0.5em' }}>
                        <TextInput
                            variant="outlined"
                            source="description"
                            style={{ minWidth: 250 }}
                            fullWidth
                            multiline
                        />
                    </Box>
                </Box>
            </>
        );
    };

    //A changer
    const CreateToolbar = (props) => {
        const classes = useStyles();
        const translate = useTranslate();
        return (
            <Toolbar className={classes.toolbar}>
                <Button label="ra.action.cancel" size="medium" onClick={handleCloseClick} className={classes.border} >
                    <Cancel />
                </Button>
                <SaveButton label={translate('app.buttons.deploy')} className={classes.buttonMarginLeft} />
            </Toolbar>
        );
    };

    return (
        <Fragment>
            <Button label={translate('app.trainingTemplates.deploy.button')} onClick={handleClick}><Send /></Button>
            <Dialog
                fullWidth
                open={showDialog}
                onClose={handleCloseClick}
                aria-label={translate('app.instanceTemplates.deploy.title')}
            >
                <DialogTitle>
                    <ViewTitle label={translate('app.instanceTemplates.deploy.title')} icon={Launch} />
                </DialogTitle>
                <DialogContent>

                    <Create>
                        <SimpleForm validate={Validate} toolbar={<CreateToolbar />} defaultValues={postDefaultValue}
                            onSubmit={DeployTemplate} >

                            <Box className={classes.flexBox}>
                                <AutocompleteInput source="pod" isRequired choices={[{ id: podId, name: podName }]}
                                    optionText="name" className={classes.templateInput} />
                            </Box>

                            <Box className={classes.flexBox} sx={{ mb: 4 }}>
                                {props.isTrainer ?
                                    <GetSelectedInstanceTemplate instanceTemplateIds={instanceTemplateIds} />
                                    :
                                    <CustomReferenceInput
                                        source="instanceTemplate"
                                        reference="instanceTemplates"
                                        label={translate('app.instanceTemplates.deploy.instanceTemplate')}
                                        filter={{ podType: type }}
                                        optionText={optionRenderer}
                                        className={classes.templateInput}
                                        filterToQuery={filterToQuery}
                                    />}

                            </Box>


                            <Box className={classes.flexBox}>
                                <FormDataConsumer>
                                    {({ formData, ...rest }) => (
                                        formData.instanceTemplate
                                            ? GetTemplate(formData.instanceTemplate)
                                            : null
                                    )}
                                </FormDataConsumer>
                            </Box>
                        </SimpleForm>
                    </Create>
                </DialogContent>
            </Dialog>
        </Fragment>
    );
};

export default DeployInstanceTemplate;

